/**
 * GateRow (J6, Wave 3) — one G1–G10 activation-gate line, MilestoneRow-style:
 * gate id, status icon (ok/attention/not_built/unknown via utils/gates
 * helpers), title, detail (+" — owner: J5" via formatGateOwnerDetail), and the
 * inline text evidence links (user-confirmed: text links per row, G7
 * drill-down requirement — every row links its evidence).
 *
 * Statuses/owners/evidence are server-computed (AutonomyGatesService) —
 * rendered as-is. Composition per
 * .design/autonomy-facade/mockups/autonomy-page.html.
 */

import React from 'react';
import Box from '@mui/material/Box';
import Link from '@mui/material/Link';
import Typography from '@mui/material/Typography';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import { AUTONOMY_TEST_IDS } from '../testIds';
import type { AutonomyGateRow as AutonomyGateRowData } from '../types/gates.types';
import { formatGateOwnerDetail, gateStatusMeta } from '../utils/gates';

export interface GateRowProps {
  gate: AutonomyGateRowData;
}

/** Status icon per the mock: ok=check, attention=!, not_built=dashed —, unknown=dotted …. */
function GateStatusIcon({ status }: { status: AutonomyGateRowData['status'] }) {
  const meta = gateStatusMeta(status);
  if (meta.severity === 'success') {
    return <CheckCircleIcon color="success" fontSize="small" aria-label={meta.label} />;
  }
  if (meta.severity === 'warning') {
    return <ErrorIcon color="warning" fontSize="small" aria-label={meta.label} />;
  }
  // Neutral circle: dashed for not_built, dotted for unknown (fail closed).
  return (
    <Box
      component="span"
      aria-label={meta.label}
      sx={{
        width: 20,
        height: 20,
        borderRadius: '50%',
        border: status === 'not_built' ? '2px dashed' : '2px dotted',
        borderColor: 'divider',
        color: 'text.secondary',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: '0.7rem',
        lineHeight: 1,
        flex: 'none',
      }}
    >
      {meta.icon}
    </Box>
  );
}

export const GateRow: React.FC<GateRowProps> = ({ gate }) => {
  return (
    <Box
      data-testid={AUTONOMY_TEST_IDS.gate(gate.gateId)}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        px: 2.5,
        py: 1.5,
        borderBottom: '1px solid',
        borderColor: 'divider',
        '&:last-of-type': { borderBottom: 'none' },
      }}
    >
      <Typography
        variant="caption"
        sx={{ fontFamily: 'monospace', color: 'text.secondary', width: 28, flex: 'none' }}
      >
        {gate.gateId}
      </Typography>
      <GateStatusIcon status={gate.status} />
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="body1">{gate.title}</Typography>
        <Typography variant="body2" color="text.secondary">
          {formatGateOwnerDetail(gate.detail, gate.owner)}
        </Typography>
      </Box>
      {gate.evidence.length > 0 && (
        <Box
          data-testid={AUTONOMY_TEST_IDS.gateEvidence(gate.gateId)}
          sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.5, flex: 'none' }}
        >
          {gate.evidence.map((evidence) => (
            <Link
              key={`${evidence.label}-${evidence.href}`}
              href={evidence.href}
              variant="caption"
              underline="hover"
              sx={{ whiteSpace: 'nowrap', textTransform: 'uppercase' }}
            >
              {evidence.label}
            </Link>
          ))}
        </Box>
      )}
    </Box>
  );
};

export default GateRow;
